import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Footer from '@/components/Footer';
import { Music, Mail, Lock, ArrowLeft, Info } from 'lucide-react';
import Logo from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthContext } from '@/App';
import { useLanguage } from '@/utils/translations';
import TwoFactorVerification from '@/components/TwoFactorVerification';
import MaintenanceBanner from '@/components/MaintenanceBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [registrationMode, setRegistrationMode] = useState('open'); // Default to open until we know
  
  // Check for registration success
  useEffect(() => {
    if (searchParams.get('registered') === 'true') {
      setSuccessMessage('Registrering gennemført! Din konto afventer godkendelse. Du kan logge ind når din konto er godkendt.');
    }
  }, [searchParams]);
  
  // Fetch registration mode on mount
  useEffect(() => {
    const fetchRegistrationMode = async () => {
      try {
        const response = await axios.get(`${API}/festivals/registration-mode`);
        setRegistrationMode(response.data.registration_mode);
      } catch (error) {
        // Default to showing signup link if we can't fetch mode
        console.log('Could not fetch registration mode, defaulting to open');
      }
    };
    fetchRegistrationMode();
  }, []);
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState(null);
  const [twoFactorMethod, setTwoFactorMethod] = useState(null);

  // Check for OAuth session on page load
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const sessionId = hashParams.get('session_id');
      
      if (sessionId) {
        setOauthLoading(true);
        try {
          const response = await axios.post(`${API}/auth/oauth/session-data`, {}, {
            headers: {
              'X-Session-ID': sessionId
            }
          });
          
          if (response.data.account_linking_required) {
            // Handle account linking
            handleAccountLinking(response.data);
          } else if (response.data.pending_approval) {
            // Account pending approval - show message and redirect to login
            const approvalType = response.data.approval_type;
            if (approvalType === 'group') {
              setError('Din konto afventer godkendelse fra din gruppeleder. Du vil modtage en email når din konto er godkendt.');
            } else {
              setError('Din konto afventer godkendelse fra en RUB CREW administrator. Du vil modtage en email når din konto er godkendt.');
            }
            setOauthLoading(false);
            // Clear URL hash
            window.history.replaceState({}, document.title, window.location.pathname);
          } else if (response.data.requires_2fa) {
            // 2FA required for Google OAuth user
            setRequires2FA(true);
            setTwoFactorUserId(response.data.user_id);
            setTwoFactorMethod(response.data.two_factor_method);
            setOauthLoading(false);
          } else {
            // Login successful - use JWT token for authentication
            await login(response.data.token, response.data.user);
            
            // Clear the URL hash
            window.history.replaceState({}, document.title, window.location.pathname);
            
            // Check for return URL (e.g., invitation page)
            const returnUrl = localStorage.getItem('returnUrl');
            if (returnUrl) {
              localStorage.removeItem('returnUrl');
              navigate(returnUrl);
              return;
            }
            
            // Fetch fresh user data to check profile completion and role
            const meResponse = await axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${response.data.token}` }
            });
            const freshUser = meResponse.data;
            
            // Redirect based on profile completion and role
            redirectAfterLogin(freshUser);
          }
        } catch (error) {
          console.error('OAuth login failed:', error);
          setError('Google login mislykkedes. Prøv venligst igen.');
        } finally {
          setOauthLoading(false);
        }
      }
    };
    
    handleOAuthCallback();
  }, [login, navigate]);

  const handleAccountLinking = async (linkingData) => {
    const confirmLink = window.confirm(
      `En konto med email ${linkingData.existing_email} findes allerede. Vil du linke den til din Google konto (${linkingData.google_name})?`
    );
    
    if (confirmLink) {
      try {
        const response = await axios.post(`${API}/auth/oauth/link-account`, {
          temp_session_data: linkingData.temp_session_data,
          confirm_linking: true
        });
        
        login(response.data.token, response.data.user);
        redirectAfterLogin(response.data.user);
      } catch (error) {
        setError('Kontolinkning mislykkedes. Prøv venligst igen.');
      }
    }
  };

  const redirectAfterLogin = (user) => {
    // Check if profile is incomplete (OAuth users need to complete profile)
    const profileIncomplete = !user.phone || !user.birthdate || !user.consent_gdpr;
    
    if (profileIncomplete && user.auth_provider === 'google') {
      // OAuth user must complete profile first
      navigate('/profile');
      return;
    }
    
    // Check for returnUrl in localStorage
    const returnUrl = localStorage.getItem('returnUrl');
    if (returnUrl) {
      localStorage.removeItem('returnUrl');
      navigate(returnUrl);
      return;
    }
    
    // Default redirect logic based on user role
    if (user.role === 'admin') {
      navigate('/admin');
    } else if (user.role === 'group_manager') {
      navigate('/gruppe-admin');
    } else if (user.role === 'workplace_supervisor') {
      navigate('/supervisor');
    } else {
      navigate('/dashboard');
    }
  };

  const handleGoogleLogin = () => {
    // Redirect back to /login page where OAuth callback handler is
    const currentUrl = window.location.origin + '/login';
    const redirectUrl = encodeURIComponent(currentUrl);
    const oauthUrl = process.env.REACT_APP_OAUTH_URL || 'https://auth.emergentagent.com';
    window.location.href = `${oauthUrl}/?redirect=${redirectUrl}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Normalize email to lowercase before sending
      const loginData = {
        ...formData,
        email: formData.email.trim().toLowerCase()
      };
      const response = await axios.post(`${API}/auth/login`, loginData, { withCredentials: true });
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setTwoFactorUserId(response.data.user_id);
        setTwoFactorMethod(response.data.two_factor_method);
        setLoading(false);
        return;
      }
      
      // Normal login (no 2FA)
      await login(response.data.token, response.data.user);
      redirectAfterLogin(response.data.user);
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      
      // Handle pending approval errors with specific messages
      if (errorDetail === 'pending_approval_group') {
        setError('🕐 Din konto afventer godkendelse fra din gruppeleder.\n\nDin gruppeleder skal godkende din tilmelding, før du kan logge ind. Du modtager en email når din konto er godkendt.\n\nHvis du har ventet længe, så kontakt din gruppeleder direkte.');
      } else if (errorDetail === 'pending_approval_admin') {
        setError('🕐 Din konto afventer godkendelse fra en administrator.\n\nDa du ikke er tilknyttet en gruppe, skal en RUB CREW administrator godkende din tilmelding. Du modtager en email når din konto er godkendt.\n\nSpørgsmål? Kontakt frivillig@rub.dk');
      } else {
        setError(errorDetail || 'Login mislykkedes. Tjek venligst dine loginoplysninger.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handle2FASuccess = async (data) => {
    // 2FA verification successful, complete login
    await login(data.token, data.user);
    redirectAfterLogin(data.user);
  };
  
  const handle2FACancel = () => {
    // Cancel 2FA and go back to login form
    setRequires2FA(false);
    setTwoFactorUserId(null);
    setTwoFactorMethod(null);
    setError('');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-200 p-4">
      {/* Maintenance Mode Banner */}
      <MaintenanceBanner />
      
      {/* Back Button - positioned absolutely */}
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          className="mb-6 text-neutral-600 hover:text-neutral-900"
          onClick={() => navigate('/')}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.backToHome')}
        </Button>
      </div>

      {/* Centered Login Card */}
      <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
        <div className="w-full max-w-md">
          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 animate-fadeIn">
          {/* Logo */}
          <div className="flex items-center justify-center mb-8">
            <Logo size="large" />
          </div>

          <h2 className="text-3xl font-bold text-center text-neutral-900 mb-2">Velkommen tilbage</h2>
          <p className="text-center text-neutral-600 mb-8">Log ind på din konto</p>

          {/* Show 2FA Verification if required */}
          {requires2FA ? (
            <TwoFactorVerification
              userId={twoFactorUserId}
              method={twoFactorMethod}
              onSuccess={handle2FASuccess}
              onCancel={handle2FACancel}
            />
          ) : (
            <>
              {successMessage && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
                  ✅ {successMessage}
                </div>
              )}

              {error && (
                <div className={`${error.includes('afventer godkendelse') ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-red-50 border-red-200 text-red-700'} border px-4 py-3 rounded-lg mb-6 whitespace-pre-line`} data-testid="error-message">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-neutral-700 font-medium mb-2 block">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10 h-12"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-neutral-700 font-medium mb-2 block">
                {t('auth.password')}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder={t('auth.password')}
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 h-12"
                  required
                  data-testid="password-input"
                />
              </div>
              <div className="mt-2 text-right">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-[#f4911c] hover:underline"
                >
                  Glemt adgangskode?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#f4911c] hover:bg-[#C41A20] text-white h-12 text-lg font-semibold"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {t('common.loading')}
                </div>
              ) : (
                t('auth.signInButton')
              )}
            </Button>
          </form>

          {/* OAuth Section */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-neutral-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-neutral-500">eller fortsæt med</span>
              </div>
            </div>

            <div className="mt-4">
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 text-neutral-700 border-neutral-300 hover:bg-neutral-50 flex items-center justify-center space-x-3"
                onClick={handleGoogleLogin}
                disabled={oauthLoading}
              >
                {oauthLoading ? (
                  <div className="flex items-center">
                    <div className="w-5 h-5 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin mr-2"></div>
                    Logger ind...
                  </div>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span className="font-medium">Google</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Email/Password Option */}
          <div className="mt-6 text-center">
            {registrationMode === 'open' ? (
              <p className="text-neutral-600">
                {t('auth.dontHaveAccount')}{' '}
                <Link to="/register" className="text-[#f4911c] hover:text-[#C41A20] font-semibold" data-testid="register-link">
                  {t('auth.joinTheCrew')}
                </Link>
              </p>
            ) : registrationMode === 'group_only' ? (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-left">
                    <p className="text-sm text-amber-800 font-medium">
                      Tilmelding kræver gruppelink
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      Har du fået et link fra din forening? Brug det til at tilmelde dig.
                      <br />
                      Spørgsmål? Kontakt{' '}
                      <a href="mailto:frivillig@rub.dk" className="text-[#f4911c] hover:underline font-medium">
                        frivillig@rub.dk
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-neutral-100 border border-neutral-200 rounded-lg">
                <p className="text-sm text-neutral-600">
                  Tilmelding er lukket. Kontakt{' '}
                  <a href="mailto:frivillig@rub.dk" className="text-[#f4911c] hover:underline font-medium">
                    frivillig@rub.dk
                  </a>{' '}
                  for hjælp.
                </p>
              </div>
            )}
          </div>
            </>
          )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default LoginPage;